import { inject } from '@angular/core';
import { CanActivateFn, Router, ActivatedRouteSnapshot } from '@angular/router';
import { AuthService } from '../../services/auth/auth.service';
import { AlertsService } from '../../services/alerts/alerts.service';
import { InvestorType } from '../../enums/investor-type.enum';
import { AlertTypeEnum } from '../../enums/alert-type.enum';
import { ROUTE_PATHS } from '../../utils/route-paths.const';

/**
 * Bank Account Guard
 * Protects bank account child routes (details, update):
 * - Accessible if user is NOT individual OR nafath gate is passed
 * - DETAILS: additionally requires has_bank_account gate passed
 *
 * Usage in routes:
 * - Apply canActivate: [bankAccountGuard] to details and update child routes
 */
export const bankAccountGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot
): boolean => {
  const authService = inject(AuthService);
  const alertsService = inject(AlertsService);
  const router = inject(Router);

  const redirectToBankAccount = (): boolean => {
    router.navigate(
      [ROUTE_PATHS.ACCOUNT, ROUTE_PATHS.BANK_ACCOUNT, ROUTE_PATHS.PREVIEW],
      {
        replaceUrl: true,
        state: { bypassGuard: true },
      }
    );
    return false;
  };

  const investor = authService.authData();
  const isIndividual = investor?.type === InvestorType.INDIVIDUAL;
  const canAccess =
    !isIndividual || alertsService.isGatePassed(AlertTypeEnum.COMPLETE_NAFATH);

  if (!canAccess) {
    return redirectToBankAccount();
  }

  const routePath = route.routeConfig?.path;

  switch (routePath) {
    case ROUTE_PATHS.DETAILS:
      // Details requires an existing bank account (gate passed)
      if (!alertsService.isGatePassed(AlertTypeEnum.HAS_BANK_ACCOUNT)) {
        return redirectToBankAccount();
      }
      return true;

    case ROUTE_PATHS.UPDATE:
      return true;

    default:
      return true;
  }
};
